import React, {useEffect, useRef, useState} from 'react';
import {requestFatca} from './fatcaApi';

const MAX_BYTES=128*1024*1024;
const dateTime=value=>value?new Date(value).toLocaleString(): '—';

export default function GiinDirectoryUpload() {
  const [directory,setDirectory]=useState(null),[file,setFile]=useState(null);
  const [loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[dragOver,setDragOver]=useState(false);
  const [error,setError]=useState(''),[success,setSuccess]=useState('');
  const inputRef=useRef(null),requestRef=useRef(null);
  const refresh=async()=>{
    requestRef.current?.abort();const controller=new AbortController();requestRef.current=controller;
    setLoading(true);setError('');
    try {setDirectory(await requestFatca('giin-directory',{signal:controller.signal}));}
    catch(e){if(e.name!=='AbortError')setError(e.message);}
    finally {if(!controller.signal.aborted)setLoading(false);}
  };
  useEffect(()=>{refresh();return()=>requestRef.current?.abort();},[]);
  const choose=files=>{
    setError('');setSuccess('');
    const selected=files?.[0];
    if(files?.length!==1 || !selected || !/\.csv$/i.test(selected.name)) {setFile(null);setError('Select one CSV file. XML, ZIP and Excel files are not accepted.');return;}
    if(!selected.size || selected.size>MAX_BYTES){setFile(null);setError('Select a non-empty CSV file up to 128 MB.');return;}
    setFile(selected);
  };
  const upload=async()=>{
    if(!file || busy)return;
    setBusy(true);setError('');setSuccess('');
    const controller=new AbortController();requestRef.current=controller;
    try {
      const result=await requestFatca(`giin-directory?${new URLSearchParams({filename:file.name})}`,{file,signal:controller.signal});
      setDirectory(result);setFile(null);if(inputRef.current)inputRef.current.value='';
      setSuccess(`${result.active.count.toLocaleString()} institutions imported. GIIN verification now uses this list.`);
    } catch(e){if(e.name!=='AbortError')setError(e.message);}
    finally {if(!controller.signal.aborted)setBusy(false);}
  };
  const active=directory?.active,disabled=busy || directory?.importing;
  return <section className="card shadow-sm mb-3" aria-labelledby="giin-directory-title">
    <div className="p-3 border-bottom d-flex align-items-center justify-content-between gap-2 flex-wrap">
      <div><div id="giin-directory-title" className="fw-bold" style={{color:'#4C5465'}}>GIIN directory — IRS FFI list</div>
        <div className="small text-muted">Upload the entire IRS CSV list to verify GIINs and retrieve registered institution names.</div></div>
      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading || busy} onClick={refresh}><i className="bi bi-arrow-repeat me-2" aria-hidden="true"/>Refresh</button>
    </div>
    <div className="p-3">
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {success && <div className="alert alert-success py-2" role="status">{success}</div>}
      {directory?.importing && <div className="alert alert-info py-2" role="status">Another upload is being processed. Refresh to see its result.</div>}
      <div className="row g-3 align-items-start">
        <div className="col-12 col-lg-4">
          <div className="text-muted small mb-2 fw-semibold">Active version</div>
          <div className="utilityIssuerImportSummary">
            {loading ? <span>Loading directory…</span> : active ? <>
              <div className="fw-semibold">{active.count.toLocaleString()} institutions</div>
              <div className="small mt-1">Uploaded: {dateTime(active.uploadedAt)}</div>
              <div className="small text-muted mt-1" style={{overflowWrap:'anywhere'}}>{active.filename}</div>
            </> : <><div className="fw-semibold">{directory?.status==='missing'?'No list uploaded':'Directory unavailable'}</div><div className="small text-muted mt-1">GIIN verification becomes available after a successful upload.</div></>}
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div style={{border:'1px solid #DDE5EA',borderRadius:8,padding:12}}>
            <div className="row g-3 align-items-end">
              <div className="col-12">
                <div className={`adminSanctionsDropzone${dragOver?' isDragOver':''}${disabled?' isDisabled':''}`}
                  onDragOver={e=>{e.preventDefault();if(!disabled)setDragOver(true);}}
                  onDragLeave={()=>setDragOver(false)}
                  onDrop={e=>{e.preventDefault();setDragOver(false);if(!disabled)choose(e.dataTransfer.files);}}>
                  <div className="adminSanctionsDropzoneIcon"><i className="bi bi-cloud-arrow-up" aria-hidden="true"/></div>
                  <label htmlFor="giin-csv-upload" className="adminSanctionsDropzoneText">Drag and drop the IRS CSV file here</label>
                  <div className="adminSanctionsDropzoneHint">CSV only · Maximum 128 MB · Columns: GIIN, FINm, CountryNm</div>
                  <input ref={inputRef} id="giin-csv-upload" type="file" className="form-control adminSanctionsFileInput" accept=".csv,text/csv" disabled={disabled} onChange={e=>choose(e.target.files)}/>
                  {file && <div className="adminSanctionsSelectedFile">{file.name} · {(file.size/1024/1024).toFixed(1)} MB</div>}
                </div>
              </div>
              <div className="col-12 d-flex justify-content-end">
                <button type="button" className="btn btn-sm btn-activation" disabled={disabled || !file} onClick={upload}>
                  <i className={`bi ${busy?'bi-hourglass-split':'bi-upload'} me-2`} aria-hidden="true"/>{busy?'Uploading and validating…':'Upload GIIN CSV'}
                </button>
              </div>
            </div>
            <div className="small text-muted mt-3">Download IRS FFI list <a href="https://apps.irs.gov/app/fatcaFfiList/flu.xhtml" target="_blank" rel="noreferrer">here</a>.</div>
          </div>
        </div>
      </div>
    </div>
  </section>;
}
